import React, { ReactElement, ReactNode } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { getStatusColor } from '../style/index';
import StatusIcon from '../atoms/icons/StatusIcon';
import InputLabel from './InputLabel';
import { ITooltip } from './types';
import { CustomStyledProps } from 'style/style';

const Container = styled('div')(
  {
    paddingBottom: '24px',
  },
  ({ customStyle }: CustomStyledProps) => customStyle && { ...customStyle }
);

const CheckboxList = styled('div')({});

const StatusMessageContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  marginTop: 0,
});

const SubLabelContainer = styled('span')`
  color: #565656;
  leading-trim: both;
  text-edge: cap;
  font-size: 13px;
  font-style: italic;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: -0.26px;
`;

const StatusMessage = styled('div')(
  ({ status, noStatusIcon }: CustomStyledProps) =>
    status && {
      color: getStatusColor()[status],
      paddingLeft: !noStatusIcon ? 4 : 0,
    }
);

const renderStatusMessage = (status, statusMessage, noStatusIcon, customErrorMessageStyle) => {
  return (
    <StatusMessageContainer style={customErrorMessageStyle}>
      {!noStatusIcon && <StatusIcon status={status} />}
      <StatusMessage status={status} noStatusIcon={noStatusIcon}>
        {statusMessage}
      </StatusMessage>
    </StatusMessageContainer>
  );
};

interface CheckboxGroupProps {
  inputLabel: any;
  labelDescription?: string;
  status: any;
  statusMessage: any;
  tooltip?: ITooltip;
  customStyle?: React.CSSProperties;
  customLabelStyle?: React.CSSProperties;
  noStatusIcon?: any;
  children: ReactNode;
  isRequired?: boolean;
  customCheckboxListStyle?: React.CSSProperties;
  customContainerStyle?: React.CSSProperties;
  subLabel?: string;
  inlineSubLabel?: boolean;
  extraLabelInfo?: React.ReactNode;
  customSubLabelStyle?: React.CSSProperties;
  customErrorMessageStyle?: React.CSSProperties;
}

const CheckboxGroup = ({
  inputLabel,
  labelDescription,
  status,
  statusMessage,
  tooltip,
  customStyle,
  customLabelStyle,
  noStatusIcon,
  children,
  isRequired = false,
  customCheckboxListStyle,
  customContainerStyle,
  subLabel,
  inlineSubLabel = false,
  extraLabelInfo,
  customSubLabelStyle,
  customErrorMessageStyle = {},
}: CheckboxGroupProps): ReactElement => {
  return (
    <Container customStyle={customStyle}>
      {inputLabel && (
        <InputLabel
          customStyle={subLabel && inlineSubLabel ? { ...customLabelStyle, ...{ marginRight: 1 } } : customLabelStyle}
          customContainerStyle={
            subLabel && inlineSubLabel ? { ...customContainerStyle, ...{ display: 'inline' } } : customContainerStyle
          }
          label={inputLabel}
          labelDescription={labelDescription}
          tooltip={tooltip ? tooltip : null}
          isRequired={isRequired}
          extraLabelInfo={extraLabelInfo}
        />
      )}
      {subLabel ? (
        <SubLabelContainer style={{ paddingLeft: inlineSubLabel ? 0 : 2, ...customSubLabelStyle }}>
          {subLabel}
        </SubLabelContainer>
      ) : null}
      <CheckboxList
        style={customCheckboxListStyle}
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        status={status}
      >
        {children}
      </CheckboxList>
      {statusMessage && status !== 'default'
        ? renderStatusMessage(status, statusMessage, noStatusIcon, customErrorMessageStyle)
        : null}
    </Container>
  );
};

CheckboxGroup.defaultProps = {
  status: 'default',
};

CheckboxGroup.propTypes = {
  inputLabel: PropTypes.string,
  labelDescription: PropTypes.string,
  /** Tooltip on right side of label */
  tooltip: PropTypes.shape({
    icon: PropTypes.any,
    text: PropTypes.string,
    overlayStyle: PropTypes.object,
    placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
  }),
  status: PropTypes.oneOf(['default', 'success', 'caution', 'error']),
  statusMessage: PropTypes.string,
};

export default CheckboxGroup;
